function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// Connect your Shopify store (COMMERCE_ONBOARDING · Shopify branch, shopify-ux-2026-08-24.html
// §178/§179). Replaces the products/customers/sales steps for Shopify tenants: one
// connection covers all three (+ inventory). Permissions explainer BEFORE the token ask
// (trust), then the Admin API token form; the honest plan gate surfaces the 402 reason
// verbatim. Same ob- design system as the rest of onboarding.

import React from "react";
import { withRouter } from "react-router";
import PubSub from "pubsub-js";
import ServiceFactory from "../../service";
import endpoints from "../../endpoints";
import account from "../../data/account";
import { AuthBase } from "../../../plunk/service/auth-base";
import { OB_CSS } from "./onboarding-css";
import { clearOnboardingCache } from "../../functions/onboarding-status";
import { rememberShop, clearPlanGateCache, getPlanGate, getShop } from "../../functions/plan-gate";
import { clearBillingStatusCache } from "../../functions/billing-status";
import { hasTenantFeature } from "../../functions/tenant-features";
const feeds = api => {
  const S = ServiceFactory.getType(`/data-feeds${api}`, "product");
  return new S();
};
const askPlunk = starter => PubSub.publish("plunk-assistant-toggle", {
  starter
});
const SCOPES = [{
  code: "read_products",
  need: "required",
  what: "Your catalog: products, variants and images. Links interest to real SKUs and powers Studio + forecasts."
}, {
  code: "read_orders",
  need: "for sales",
  what: "Orders become sales analytics: top sellers, forecasts, store performance."
}, {
  code: "read_customers",
  need: "for customers",
  what: "Customers become Contacts with lifecycle stages. Skip if you only want product analytics."
}, {
  code: "write_products",
  need: "optional",
  what: "Only if you want Studio to deliver finished renders back to your store."
}];

// ShopifyService sends a failed install/open back here as /Shopify?shopify=error&reason=<code>&shop= (2026-11-03, review
// item 18: the merchant's browser never sees a raw JSON error). Short codes, plain words.
const ERROR_REASONS = {
  state: "This install link is no longer valid. Links are single-use and expire after 10 minutes.",
  hmac: "Shopify's signature on the install did not check out, so nothing was connected.",
  token_exchange: "Shopify did not hand over access to the store, so nothing was connected.",
  invalid_callback: "The install came back incomplete, so nothing was connected.",
  failed: "We hit a problem connecting your store. Our team has been notified."
};
const APP_ORIGIN_RE = /^https:\/\/(app|staging)\.plunk\.io/i;
function sameOrigin(url) {
  try {
    const here = window.location.origin;
    if (!APP_ORIGIN_RE.test(here)) return url;
    const u = new URL(url, here);
    if (APP_ORIGIN_RE.test(u.origin) && u.origin !== here) return here + u.pathname + u.search + u.hash;
    const r = u.searchParams.get("redirect");
    if (r && APP_ORIGIN_RE.test(r)) {
      const t = new URL(r);
      if (t.origin !== here) {
        u.searchParams.set("redirect", here + t.pathname + t.search + t.hash);
        return u.toString();
      }
    }
    return url;
  } catch (e) {
    return url;
  }
}
class ShopifyConnectView extends React.Component {
  constructor(props) {
    super(props);
    // Cold App-Store install (model A): ShopifyService GET /shopify bounces the
    // merchant here with ?shop=<domain>. Prefill it and jump straight to the
    // connect step so they can authorize immediately after sign-in/sign-up.
    // Plan step: unlock on its own once Billing sees the Commerce plan Shopify reports after approval (no "I've
    // already picked one" button, DS 2026-10-22). Checks every 5 s while the page is visible, and on focus.
    _defineProperty(this, "pollPlan", async () => {
      if (!this._alive || this.state.phase !== "plan" || this.state.uninstalled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      clearPlanGateCache();
      clearBillingStatusCache();
      const g = await getPlanGate(true).catch(() => null);
      if (!this._alive || !g) return;
      if (g.uninstalled) {
        this.setState({
          uninstalled: true
        });
        return;
      }
      if (!g.gated) {
        document.body.classList.remove("plan-gated");
        this.props.history.push("/Setup");
      }
    });
    // Plan step, the other half (2026-11-03, review item 9): ask ShopifyService to read the store's live plan and mirror
    // it as confirmed by the merchant (POST /shopify/plan/confirm). That is the only way a plan ended by an uninstall
    // comes back, and it covers a dropped app_subscriptions/update webhook: a paying merchant must never sit on
    // "Choose your plan". Runs on mount and every time the window regains focus (the merchant returns from Shopify).
    _defineProperty(this, "confirmPlan", async () => {
      if (!this._alive || this.state.phase !== "plan" || this.state.uninstalled || this._confirming) return this.pollPlan();
      const shop = String(this.state.shop || getShop() || "").trim().toLowerCase();
      let tenant = "";
      try {
        tenant = (account.get() || {}).tenantId || "";
      } catch (e) {/* pre-render */}
      if (!shop || !tenant || !AuthBase.idToken) return this.pollPlan();
      this._confirming = true;
      try {
        const host = endpoints && endpoints.product && endpoints.product.host || "api.plunk.io";
        const res = await fetch(`https://${host}/shopify/plan/confirm?shop=${encodeURIComponent(shop)}`, {
          method: "POST",
          headers: {
            "Authorization": "Bearer " + AuthBase.idToken,
            "x-app-id": tenant
          }
        });
        const d = await res.json().catch(() => ({}));
        if (this._alive && res.ok && d.active) {
          clearPlanGateCache();
          clearBillingStatusCache();
        }
      } catch (e) {/* the poll below still runs */} finally {
        this._confirming = false;
      }
      return this.pollPlan();
    });
    // The provision call goes to whichever ShopifyService this bundle is pinned to, and that
    // service composes the post-handoff landing from ITS App base URL. A merchant who started
    // on app.plunk.io must land back on app.plunk.io, so rewrite any plunk.io App origin in the
    // handoff (or its redirect= target) to the origin the merchant is actually on.
    _defineProperty(this, "provision", async () => {
      const host = endpoints && endpoints.product && endpoints.product.host || "api.plunk.io";
      this.setState({
        provisionError: null
      });
      try {
        const res = await fetch(`https://${host}/shopify/provision`, {
          method: "POST",
          headers: {
            "content-type": "application/json"
          },
          body: JSON.stringify({
            pt: this.state.pt
          })
        });
        const d = await res.json().catch(() => ({}));
        if (!this._alive) return;
        if (res.status === 410) {
          this.setState({
            provisionError: "This setup link has expired. Open Plunk from your Shopify admin to continue."
          });
          return;
        }
        if (!res.ok) {
          this.setState({
            provisionError: d.error || `Setup failed (${res.status}).`
          });
          return;
        }
        window.location.replace(sameOrigin(d.handoffUrl || d.redirect || "/Setup"));
      } catch (e) {
        if (this._alive) this.setState({
          provisionError: "We couldn't reach the setup service. Check your connection and retry."
        });
      }
    });
    _defineProperty(this, "normalizeShop", () => this.state.shop.trim().replace(/^https?:\/\//i, "").replace(/\/$/, ""));
    // Real-time path: hand off to the public Shopify app's OAuth install
    // (ShopifyService api.plunk.io/shopify/install). Security fix 2026-10-12: the
    // workspace no longer rides the URL; ShopifyService mints a signed, 10-minute,
    // single-use install link for the signed-in caller (GET /shopify/install-url).
    // The callback registers webhooks and redirects to /Manage/Connections.
    _defineProperty(this, "startOAuth", async () => {
      const shop = this.normalizeShop();
      if (!/^[a-zA-Z0-9][a-zA-Z0-9\-]*\.myshopify\.com$/.test(shop)) {
        this.setState({
          error: "Enter your full store domain, e.g. your-store.myshopify.com"
        });
        return;
      }
      let tenant = "";
      try {
        tenant = (account.get() || {}).tenantId || "";
      } catch (e) {/* pre-render */}
      if (!tenant || !AuthBase.idToken) {
        this.setState({
          error: "Couldn't determine your workspace. Reload and try again."
        });
        return;
      }
      const host = endpoints && endpoints.product && endpoints.product.host || "api.plunk.io";
      this.setState({
        error: null
      });
      try {
        const res = await fetch(`https://${host}/shopify/install-url?shop=${encodeURIComponent(shop)}`, {
          headers: {
            "Authorization": "Bearer " + AuthBase.idToken,
            "x-app-id": tenant
          }
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || !d.url) {
          this.setState({
            error: d.error || `Couldn't start the Shopify connection (${res.status}).`
          });
          return;
        }
        window.location.href = d.url;
      } catch (e) {
        this.setState({
          error: "We couldn't reach the Shopify connection service. Check your connection and retry."
        });
      }
    });
    _defineProperty(this, "connect", async () => {
      const shop = this.normalizeShop();
      const token = this.state.token.trim();
      if (!shop || !token) {
        this.setState({
          error: "Enter your store domain and Admin API token."
        });
        return;
      }
      this.setState({
        busy: true,
        error: null,
        planGate: null
      });
      try {
        await feeds("/schedules").postAsync({
          provider: "Shopify",
          mode: "full-refresh",
          frequency: "Daily",
          hour: 4,
          timezone: "UTC",
          status: "Active",
          // Locked to the ShopifyFeedProvider contract: ShopifyShopDomain + ShopifyAccessToken
          // (provider throws if either is missing; ShopifyApiVersion + ShopifySync* default).
          settings: JSON.stringify({
            ShopifyShopDomain: shop,
            ShopifyAccessToken: token
          })
        });
        clearOnboardingCache();
        if (this._alive) this.setState({
          busy: false,
          done: true
        });
      } catch (e) {
        // Surface the plan gate (402 plan_required) honestly; otherwise show the message.
        let msg = "Couldn't connect the store. Check the domain and token and try again.";
        let plan = null;
        try {
          const body = JSON.parse(e && e.message ? e.message : "{}");
          if (body && body.error) msg = body.error;
          if (body && (body.code === "plan_required" || /plan/i.test(body.error || ""))) plan = body.error || "This sync isn't included in your current plan.";
        } catch (x) {
          if (e && typeof e.message === "string" && /plan/i.test(e.message)) plan = e.message;
        }
        if (this._alive) this.setState({
          busy: false,
          error: plan ? null : msg,
          planGate: plan
        });
      }
    });
    let _shop = "",
      pt = "",
      phase = "",
      reason = "";
    try {
      const q = new URLSearchParams(window.location.search || "");
      _shop = (q.get("shop") || "").trim();
      pt = (q.get("pt") || "").trim();
      phase = (q.get("shopify") || "").trim();
      reason = (q.get("reason") || "").trim();
    } catch (e) {/* noop */}
    // Shopify tenants never see the token-paste form (2026-11-03, review item 14): a store known from the URL, the
    // remembered install or the tenant's shopify feature goes straight to Connect with Shopify, domain shown, not typed.
    let remembered = "";
    try {
      remembered = getShop();
    } catch (e) {/* noop */}
    let shopifyTenant = false;
    try {
      shopifyTenant = hasTenantFeature("shopify");
    } catch (e) {/* noop */}
    const known = _shop || remembered;
    shopifyTenant = shopifyTenant || !!known;
    // Merchant-first install (2026-09-25): ?shopify=provisioning&pt= — the store is parked,
    // we mint the workspace and follow the sign-in handoff; ?shopify=plan — signed in,
    // the plan is picked on Shopify's Managed Pricing page.
    this.state = {
      stage: known ? "connect" : "explain",
      shop: known,
      token: "",
      busy: false,
      error: null,
      planGate: null,
      done: false,
      phase: pt ? "provisioning" : phase,
      pt,
      provisionError: null,
      attempt: 0,
      planCheck: null,
      reason,
      shopifyTenant,
      knownShop: !!known,
      editShop: false
    };
    this._alive = true;
    // The plan gate (functions/plan-gate) keys off the remembered store; every landing here
    // carries ?shop=. A ?shopify=connected landing means a plan was just picked → re-check.
    if (_shop) rememberShop(_shop);
    if (phase === "connected" || phase === "plan") {
      clearPlanGateCache();
      clearBillingStatusCache();
    }
  }
  componentWillUnmount() {
    this._alive = false;
    clearInterval(this._planT);
    if (this._onFocus) window.removeEventListener("focus", this._onFocus);
  }
  componentDidMount() {
    if (this.state.phase === "provisioning") this.provision();
    // Plan step: was the app uninstalled? Then the only way forward is reinstalling from Shopify.
    if (this.state.phase === "plan") {
      getPlanGate(true).then(g => {
        if (this._alive && g) this.setState({
          uninstalled: !!g.uninstalled,
          shop: this.state.shop || g.shop || ""
        });
      }).catch(() => {}).then(() => this.confirmPlan());
      this._planT = setInterval(this.pollPlan, 5000);
      this._onFocus = () => this.confirmPlan();
      window.addEventListener("focus", this._onFocus);
    }
  }
  render() {
    const {
      stage,
      shop,
      token,
      busy,
      error,
      planGate,
      done,
      phase,
      provisionError,
      shopifyTenant,
      knownShop,
      editShop,
      reason
    } = this.state;
    // ?shopify=link&shop= (2026-11-03): the store's contact email already belongs to a Plunk workspace, so the
    // install was not attached automatically — the merchant signs in here and connects the store under their own
    // session (the signed install-url flow). One Shopify store per workspace.
    const linkNotice = phase === "link" && shop ? /*#__PURE__*/React.createElement("div", {
      className: "ob-note",
      style: {
        margin: "0 0 14px",
        padding: "12px 14px",
        background: "#fff8e6",
        border: "1px solid #f2d99a",
        borderRadius: 8,
        fontSize: 13
      }
    }, /*#__PURE__*/React.createElement("b", null, "Almost there."), " ", shop, " wasn't connected automatically because its contact email already belongs to a Plunk workspace. You're signed in now: click ", /*#__PURE__*/React.createElement("b", null, "Connect with Shopify"), " to add this store to this workspace. If this workspace already has a different store connected, install with a different contact email to get a separate workspace.", /*#__PURE__*/React.createElement("span", {
      style: {
        display: "block",
        marginTop: 6
      }
    }, "Is the store's email a Plunk login that an earlier store install created, and you never set a password for it? Sign out, then use ", /*#__PURE__*/React.createElement("b", null, "Forgot password"), " on the sign-in page with that email.")) : null;
    const store = String(shop || "").replace(/\.myshopify\.com$/i, "");
    if (phase === "error") {
      return /*#__PURE__*/React.createElement("div", {
        className: "ob-root"
      }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement("div", {
        className: "ob-wrap",
        style: {
          maxWidth: 720
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-sec",
        style: {
          textAlign: "center",
          padding: "34px 24px"
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-mark",
        style: {
          margin: "0 auto 14px"
        }
      }, /*#__PURE__*/React.createElement("i", {
        className: "material-icons",
        style: {
          fontSize: 26
        }
      }, "error_outline")), /*#__PURE__*/React.createElement("div", {
        className: "lab",
        style: {
          fontSize: 16
        }
      }, "Couldn't finish connecting", shop ? ` ${shop}` : " your store"), /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          maxWidth: "52ch",
          margin: "8px auto 0"
        }
      }, ERROR_REASONS[reason] || "Something went wrong finishing the Shopify connection.", " ", reason === "failed" ? "Retry in a moment, or email support@plunk.io." : "Start the connection again from here, or open Plunk from your Shopify admin."), /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 16,
          display: "flex",
          gap: 10,
          justifyContent: "center",
          flexWrap: "wrap"
        }
      }, knownShop ? /*#__PURE__*/React.createElement("button", {
        className: "ob-btn",
        onClick: this.startOAuth
      }, "Try again") : /*#__PURE__*/React.createElement("button", {
        className: "ob-btn",
        onClick: () => this.setState({
          phase: "",
          stage: "connect",
          error: null
        })
      }, "Try again"), /*#__PURE__*/React.createElement("button", {
        className: "ob-btn ghost",
        onClick: () => this.props.history.push("/Setup")
      }, "Back to setup")), error && /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          marginTop: 12,
          color: "var(--ob-accent, #ff6358)"
        }
      }, error))));
    }
    if (phase === "provisioning") {
      return /*#__PURE__*/React.createElement("div", {
        className: "ob-root"
      }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement("div", {
        className: "ob-wrap",
        style: {
          maxWidth: 720
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-sec",
        style: {
          textAlign: "center",
          padding: "34px 24px"
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-mark",
        style: {
          margin: "0 auto 14px"
        }
      }, /*#__PURE__*/React.createElement("i", {
        className: "material-icons",
        style: {
          fontSize: 26
        }
      }, provisionError ? "error_outline" : "hourglass_top")), /*#__PURE__*/React.createElement("div", {
        className: "lab",
        style: {
          fontSize: 16
        }
      }, provisionError ? "Setup hit a snag" : "Setting up your Plunk workspace…"), /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          maxWidth: "48ch",
          margin: "8px auto 0"
        }
      }, provisionError || `We're creating a workspace for ${shop || "your store"} and signing you in. This takes about half a minute.`), provisionError && /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 16
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: "ob-btn",
        onClick: this.provision
      }, "Retry")))));
    }
    if (phase === "plan" && this.state.uninstalled) {
      return /*#__PURE__*/React.createElement("div", {
        className: "ob-root"
      }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement("div", {
        className: "ob-wrap",
        style: {
          maxWidth: 720
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-sec",
        style: {
          textAlign: "center",
          padding: "34px 24px"
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-mark",
        style: {
          margin: "0 auto 14px"
        }
      }, /*#__PURE__*/React.createElement("i", {
        className: "material-icons",
        style: {
          fontSize: 26
        }
      }, "storefront")), /*#__PURE__*/React.createElement("div", {
        className: "lab",
        style: {
          fontSize: 16
        }
      }, "Plunk Commerce was uninstalled", shop ? ` from ${shop}` : ""), /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          maxWidth: "52ch",
          margin: "8px auto 0"
        }
      }, "Reinstall it from Shopify to keep using this workspace. You'll choose your plan in Shopify again. Your data is kept for 30 days after uninstalling."), /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 16,
          display: "flex",
          gap: 10,
          justifyContent: "center",
          flexWrap: "wrap"
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: "ob-btn",
        disabled: !shop,
        onClick: this.startOAuth
      }, "Reinstall Plunk Commerce")), error && /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          marginTop: 12,
          color: "var(--ob-accent, #ff6358)"
        }
      }, error))));
    }
    if (phase === "plan") {
      return /*#__PURE__*/React.createElement("div", {
        className: "ob-root"
      }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement("div", {
        className: "ob-wrap",
        style: {
          maxWidth: 720
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-sec",
        style: {
          textAlign: "center",
          padding: "34px 24px"
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-mark",
        style: {
          margin: "0 auto 14px"
        }
      }, /*#__PURE__*/React.createElement("i", {
        className: "material-icons",
        style: {
          fontSize: 26
        }
      }, "storefront")), /*#__PURE__*/React.createElement("div", {
        className: "lab",
        style: {
          fontSize: 16
        }
      }, "Your store is connected. Choose your plan"), /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          maxWidth: "52ch",
          margin: "8px auto 0"
        }
      }, "Plunk Commerce is billed through Shopify on your store's invoice; the plan page shows the price. Pick your plan in Shopify and you'll land back here with everything unlocked; your first product, order and customer sync starts right away."), /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 16,
          display: "flex",
          gap: 10,
          justifyContent: "center",
          flexWrap: "wrap"
        }
      }, /*#__PURE__*/React.createElement("a", {
        className: "ob-btn",
        href: `https://admin.shopify.com/store/${encodeURIComponent(store)}/charges/plunk-commerce/pricing_plans`
      }, "Choose plan in Shopify")), /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          marginTop: 12
        }
      }, "After you approve the plan, this page unlocks on its own within a few seconds."))));
    }
    if (done) {
      return /*#__PURE__*/React.createElement("div", {
        className: "ob-root"
      }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement("div", {
        className: "ob-wrap",
        style: {
          maxWidth: 720
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-sec",
        style: {
          textAlign: "center",
          padding: "34px 24px"
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ob-mark",
        style: {
          margin: "0 auto 14px"
        }
      }, /*#__PURE__*/React.createElement("i", {
        className: "material-icons",
        style: {
          fontSize: 26
        }
      }, "check")), /*#__PURE__*/React.createElement("div", {
        className: "lab",
        style: {
          fontSize: 16
        }
      }, "Shopify connected"), /*#__PURE__*/React.createElement("p", {
        className: "ob-hint",
        style: {
          maxWidth: "48ch",
          margin: "8px auto 0"
        }
      }, "Your first sync is running: products import now, orders and customers follow. This can take a few minutes; the step flips to done automatically."), /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 16
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: "ob-btn",
        onClick: () => this.props.history.push("/Setup")
      }, "Back to setup")))));
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "ob-root"
    }, /*#__PURE__*/React.createElement("style", null, OB_CSS), /*#__PURE__*/React.createElement("div", {
      className: "ob-wrap",
      style: {
        maxWidth: 720
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "ob-eyebrow"
    }, "Setup \xB7 commerce \xB7 Shopify"), /*#__PURE__*/React.createElement("div", {
      className: "ob-hrow"
    }, /*#__PURE__*/React.createElement("h2", null, "Connect your Shopify store")), /*#__PURE__*/React.createElement("p", {
      className: "ob-next",
      style: {
        color: "var(--ob-muted)"
      }
    }, "One connection brings in your products, orders (as sales analytics), customers and inventory. Nothing is written to your store unless you grant it."), /*#__PURE__*/React.createElement("div", {
      className: "ob-steps",
      style: {
        marginTop: 14
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: `s ${stage === "explain" ? "now" : "done"}`
    }, /*#__PURE__*/React.createElement("b", null, "What we access", stage === "connect" ? " ✓" : ""), "the permissions, first"), /*#__PURE__*/React.createElement("div", {
      className: `s ${stage === "connect" ? "now" : ""}`
    }, /*#__PURE__*/React.createElement("b", null, "Connect"), shopifyTenant ? "approve it in Shopify" : "paste your Admin API token"), /*#__PURE__*/React.createElement("div", {
      className: "s"
    }, /*#__PURE__*/React.createElement("b", null, "Sync"), "products, orders, customers")), stage === "explain" && /*#__PURE__*/React.createElement("div", {
      className: "ob-sec"
    }, /*#__PURE__*/React.createElement("span", {
      className: "lab"
    }, "What Plunk needs access to"), /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        margin: "4px 0 12px"
      }
    }, "We ask for the least we can. You ", shopifyTenant ? "approve these in Shopify when you connect" : "grant these when you create the app in Shopify", ", and you can revoke any time from your store's app settings."), SCOPES.map(s => /*#__PURE__*/React.createElement("div", {
      key: s.code,
      style: {
        display: "flex",
        gap: 12,
        padding: "9px 0",
        borderTop: "1px solid var(--ob-border)"
      }
    }, /*#__PURE__*/React.createElement("code", {
      style: {
        fontFamily: "ui-monospace,monospace",
        fontSize: 12,
        color: "var(--ob-fg-strong)",
        minWidth: 130
      }
    }, s.code), /*#__PURE__*/React.createElement("span", {
      style: {
        flex: 1,
        fontSize: 12.5,
        color: "var(--ob-muted)"
      }
    }, s.what), /*#__PURE__*/React.createElement("span", {
      className: `ob-pill${s.need === "required" ? " warn" : ""}`,
      style: {
        alignSelf: "flex-start"
      }
    }, s.need))), /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        marginTop: 12
      }
    }, /*#__PURE__*/React.createElement("b", null, "We never"), " read your storefront theme, staff accounts, or payment/payout details, and we never write to your store without ", /*#__PURE__*/React.createElement("code", null, "write_products"), ", which is optional."), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 14,
        display: "flex",
        gap: 8,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn",
      onClick: () => this.setState({
        stage: "connect"
      })
    }, "Continue to connect"), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.props.history.push("/Setup")
    }, "\u2190 Back to setup"))), stage === "connect" && linkNotice, stage === "connect" && /*#__PURE__*/React.createElement("div", {
      className: "ob-sec"
    }, /*#__PURE__*/React.createElement("span", {
      className: "lab"
    }, "Connect your store"), /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        margin: "4px 0 12px"
      }
    }, shopifyTenant ? "Connect with one click. You approve the permissions in Shopify, and products, orders, customers and inventory sync in real time." : "Enter your store domain, then connect with one click, or paste an Admin API token for a read-only nightly feed."), knownShop && !editShop ? /*#__PURE__*/React.createElement("div", {
      style: {
        margin: "10px 0",
        display: "flex",
        alignItems: "baseline",
        gap: 10,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: ".05em",
        color: "var(--ob-muted)"
      }
    }, "STORE"), /*#__PURE__*/React.createElement("b", {
      style: {
        fontSize: 14,
        color: "var(--ob-fg-strong)"
      }
    }, shop), /*#__PURE__*/React.createElement("span", {
      className: "ob-ask-link",
      onClick: () => this.setState({
        editShop: true
      })
    }, "Not this store?")) : /*#__PURE__*/React.createElement("div", {
      style: {
        margin: "10px 0"
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: {
        display: "block",
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: ".05em",
        color: "var(--ob-muted)",
        marginBottom: 5
      }
    }, "STORE DOMAIN"), /*#__PURE__*/React.createElement("input", {
      className: "ob-input",
      value: shop,
      placeholder: "your-store.myshopify.com",
      onChange: e => this.setState({
        shop: e.target.value,
        error: null
      })
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 8,
        padding: "14px 16px",
        borderRadius: 12,
        border: "1px solid var(--ob-border)"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "lab",
      style: {
        margin: 0
      }
    }, "Connect with Shopify"), !shopifyTenant && /*#__PURE__*/React.createElement("span", {
      className: "ob-pill"
    }, "recommended \xB7 real-time")), /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        margin: "6px 0 10px"
      }
    }, "Install Plunk on your store in one click. You approve the permissions in Shopify, and products, orders, customers and inventory sync in real time via webhooks."), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn",
      disabled: busy,
      onClick: this.startOAuth
    }, "Connect with Shopify"), shopifyTenant && error && /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        margin: "10px 0 0",
        color: "var(--ob-warn)"
      }
    }, error)), shopifyTenant && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 12,
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.setState({
        stage: "explain",
        error: null
      })
    }, "\u2190 Permissions"), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.props.history.push("/Setup")
    }, "Back to setup"), /*#__PURE__*/React.createElement("span", {
      className: "ob-ask-link",
      onClick: () => askPlunk("How do I connect my Shopify store?")
    }, "or ask Plunk")), !shopifyTenant && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "16px 0"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        height: 1,
        background: "var(--ob-border)"
      }
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 11,
        color: "var(--ob-muted)"
      }
    }, "or use an Admin API token"), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        height: 1,
        background: "var(--ob-border)"
      }
    })), /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        margin: "0 0 10px"
      }
    }, "Prefer a read-only nightly feed? In Shopify: ", /*#__PURE__*/React.createElement("b", null, "Settings \u2192 Apps and sales channels \u2192 Develop apps \u2192 Create app"), ", grant", /*#__PURE__*/React.createElement("code", null, " read_products"), ", ", /*#__PURE__*/React.createElement("code", null, "read_orders"), ", ", /*#__PURE__*/React.createElement("code", null, "read_customers"), "(add ", /*#__PURE__*/React.createElement("code", null, "write_products"), " for Studio render delivery), install, and paste the ", /*#__PURE__*/React.createElement("b", null, "Admin API access token"), "."), /*#__PURE__*/React.createElement("div", {
      style: {
        margin: "10px 0"
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: {
        display: "block",
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: ".05em",
        color: "var(--ob-muted)",
        marginBottom: 5
      }
    }, "ADMIN API ACCESS TOKEN"), /*#__PURE__*/React.createElement("input", {
      className: "ob-input",
      type: "password",
      value: token,
      placeholder: "shpat-\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
      onChange: e => this.setState({
        token: e.target.value
      })
    })), error && /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        color: "var(--ob-warn)"
      }
    }, error), planGate && /*#__PURE__*/React.createElement("div", {
      style: {
        margin: "10px 0",
        padding: "12px 14px",
        borderRadius: 11,
        border: "1px solid var(--ob-warn)",
        background: "var(--ob-warn-bg)",
        fontSize: 12.5
      }
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        color: "var(--ob-fg-strong)"
      }
    }, "Plan required."), " ", planGate, /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 8
      }
    }, /*#__PURE__*/React.createElement("a", {
      className: "ob-walk",
      style: {
        margin: 0
      },
      href: "mailto:support@plunk.io?subject=Upgrade%20to%20connect%20Shopify"
    }, "Talk to us about upgrading \u2192"))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 12,
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost",
      disabled: busy,
      onClick: this.connect
    }, busy ? "Connecting…" : "Connect with token (nightly)"), /*#__PURE__*/React.createElement("button", {
      className: "ob-btn ghost sm",
      onClick: () => this.setState({
        stage: "explain",
        error: null,
        planGate: null
      })
    }, "\u2190 Permissions"), /*#__PURE__*/React.createElement("span", {
      className: "ob-ask-link",
      onClick: () => askPlunk("How do I create a Shopify Admin API token?")
    }, "or ask Plunk")), /*#__PURE__*/React.createElement("p", {
      className: "ob-hint",
      style: {
        marginTop: 10
      }
    }, "Your token is stored encrypted and used only to read the data above. Prefer a hosted feed or custom API instead? ", /*#__PURE__*/React.createElement("a", {
      className: "ob-walk",
      style: {
        margin: 0
      },
      onClick: () => this.props.history.push("/Data"),
      role: "button"
    }, "Use Manage Data \u2192"))))));
  }
}
export default withRouter(ShopifyConnectView);
export const ShopifyConnectViewComponent = withRouter(ShopifyConnectView);